import type { OnInit } from '@angular/core';
import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, EventType, Router, RouterModule } from '@angular/router';
import type { EAgentKey } from '@dev-fast/types';
import { OneSignalService } from 'one-signal';
import { filter, interval, map, startWith, switchMap, tap } from 'rxjs';
import { SwService } from 'sw-service';

import { CookieService } from '@app/core/cookie-service';
import { LayoutTypeService } from '@app/core/layout-service';
import { LoadingService } from '@app/core/loading-service';
import { SeoService } from '@app/core/seo-service';
import { SocketService } from '@app/core/socket-io';
import { LoadingProgressBarComponent } from '@app/shared/components';
import { IS_SERVER_TOKEN } from '@app/shared/utils';
import { WINDOW } from '@app/shared/utils/window';
import { replacementAnimation } from '@app/ui/animations';
import { UiIconsService } from '@app/ui/icons';

import { environment } from '../environments/environment';

@Component({
  selector: 'app-new-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterModule, LoadingProgressBarComponent],
  animations: [replacementAnimation],
})
export class AppComponent implements OnInit {
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #uiIconsService = inject(UiIconsService);
  readonly #oneSignalService = inject(OneSignalService);
  readonly #cookieService = inject(CookieService);
  readonly #isServer = inject(IS_SERVER_TOKEN);
  readonly #seoService = inject(SeoService);
  readonly #socketService = inject(SocketService);
  readonly #loadingService = inject(LoadingService);
  readonly #layoutService = inject(LayoutTypeService);
  readonly #window = inject(WINDOW);

  readonly loadingProgress = toSignal<number | undefined>(this.#loadingService.loadingProgress$);
  readonly #swService = inject(SwService);

  // Observable для отслеживания onlineAgentKey из активного роута
  readonly onlineAgentKey$ = this.#router.events.pipe(
    filter((event) => event.type === EventType.NavigationEnd),
    startWith(null), // Запускаем сразу при инициализации
    map(() => this.#getOnlineAgentKeyFromActiveRoute()),
    switchMap((agentKey: EAgentKey | null) => {
      return interval(120_000).pipe(
        tap(() => {
          if (!agentKey) {
            return;
          }
          this.#layoutService.pingOnlineByMode(agentKey);
        }),
      );
    }),
  );

  constructor() {
    this.#uiIconsService.registerIcons();
    this.#seoService.start();
    if (this.#isServer) {
      return;
    }
    this.#handleNonServerTasks();
  }

  ngOnInit(): void {
    if (this.#isServer) {
      return;
    }
    // this.coreLicenseService.appendLicenseScript();
    this.#initOnlineCount();
  }

  #initOnlineCount(): void {
    // Подписываемся на изменения onlineAgentKey для отладки
    this.onlineAgentKey$.subscribe();
  }

  #getOnlineAgentKeyFromActiveRoute(): EAgentKey | null {
    let route = this.#activatedRoute;
    // Проходим по дереву роутов до самого активного листового роута
    while (route.firstChild) {
      route = route.firstChild;
    }

    // Ищем onlineAgentKey в данных текущего роута или его родителей
    let currentRoute: ActivatedRoute | null = route;
    while (currentRoute) {
      const agentKey = currentRoute.snapshot.data['onlineAgentKey'];
      if (agentKey) {
        return agentKey;
      }
      currentRoute = currentRoute.parent;
    }

    return null;
  }

  #handleNonServerTasks(): void {
    this.#redirectIfHttp();
    this.#setCookie();
    this.#socketService.monitorSocket();
    this.#oneSignalService.init();
    this.#swService.start();
  }

  #redirectIfHttp(): void {
    if (this.#window?.location.protocol === 'http:' && environment.production) {
      this.#window.location.href = location.href.replace('http', 'https');
    }
  }

  #setCookie(): void {
    this.#cookieService.setCookie('angularHost', this.#window!.location.host, {
      path: '/',
      expires: 'Tue, 19 Jan 2038 03:14:07 GMT',
    });
  }
}
